import { LoadingButton } from "@mui/lab";
import {
  Alert,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  TextField,
} from "@mui/material";
import { isEmpty } from "lodash";
import { useState } from "react";
import { useUser } from "reactfire";
import firebase from "firebase/app";

export default function UpdateEmail({
  isOpen,
  handleClose,
}: {
  isOpen: boolean;
  handleClose: () => void;
}): React.ReactElement {
  const { data: user } = useUser();
  const [email, setEmail] = useState(user?.email || "");
  const [error, setError] = useState("");
  const [isSuccess, setIsSuccess] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const resetAndClose = () => {
    setEmail(user?.email || "");
    setIsSuccess(false);
    setIsSubmitting(false);
    setIsLoginRequired(false);
    setError("");
    handleClose();
  };

  // these are used when the updateEmail call returns an error
  // that requires the user to reauthenticate: "auth/requires-recent-login"
  const [isLoginRequired, setIsLoginRequired] = useState(false);
  const [password, setPassword] = useState("");

  const handleUpdate = async () => {
    setIsSubmitting(true);
    setIsLoginRequired(false);
    setError("");
    setIsSuccess(false);
    try {
      if (!isEmpty(password)) {
        const credential = firebase.auth.EmailAuthProvider.credential(
          user.email || "",
          password
        );
        await user?.reauthenticateWithCredential(credential);
        console.log("success! reauthenticated", credential);
      }
      // this may throw an error
      await user.updateEmail(email);
      console.log("success! email updated");
      setIsSuccess(true);
    } catch (error) {
      if (error.code === "auth/requires-recent-login") {
        setIsLoginRequired(true);
      } else if (error.code === "auth/invalid-email") {
        setError("Invalid email address.");
      } else if (error.code === "auth/email-already-in-use") {
        setError("Email address already in use.");
      } else if (error.code === "auth/too-many-requests") {
        setError("Too many requests. Please try again later.");
      } else {
        // unknown error
        setError(error.message);
      }

      setIsSuccess(false);
      console.log(error);
    } finally {
      setIsSubmitting(false);
    }
  };
  const handleKeyPress = (e: React.KeyboardEvent<HTMLDivElement>) => {
    if (e.key === "Enter" && email !== user?.email) {
      handleUpdate();
    }
  };

  return (
    <Dialog open={isOpen} maxWidth="sm">
      <DialogTitle>Update Your Email</DialogTitle>
      <DialogContent>
        <DialogContentText sx={{ mb: 1 }}>
          To update your email, please enter your new email address below.
        </DialogContentText>
        <TextField
          sx={{ my: 1 }}
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          onKeyPress={handleKeyPress}
          autoFocus
          label="New Email"
          type="email"
          fullWidth
        />

        {isLoginRequired ? (
          <>
            <TextField
              sx={{ mt: 1 }}
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              onKeyPress={handleKeyPress}
              label="Password"
              type="password"
              fullWidth
            />
            <Alert severity="warning" sx={{ mt: 2 }}>
              Please enter your password in order to update your email.
            </Alert>
          </>
        ) : null}

        {isSuccess ? (
          <Alert
            action={
              <Button color="inherit" size="small" onClick={resetAndClose}>
                Close
              </Button>
            }
            sx={{ my: 1 }}
            severity="success"
          >
            Email updated! Please check your inbox for a verification email.
            Note: you may have to re-login on other devices.
          </Alert>
        ) : null}
        {!isEmpty(error) ? (
          <Alert sx={{ my: 1 }} severity="error">
            {error}
          </Alert>
        ) : null}
      </DialogContent>
      <DialogActions>
        <Button onClick={resetAndClose}>Cancel</Button>
        <LoadingButton
          loading={isSubmitting}
          disabled={email === user?.email}
          variant="contained"
          onClick={handleUpdate}
        >
          Update Email
        </LoadingButton>
      </DialogActions>
    </Dialog>
  );
}
